import { useEffect, useRef, useTransition, useMemo } from 'react'
import dayjs, { DayjsInput } from 'utils/dayjs'
import { UseEventsForMultiArtistOptions } from '../types'
import { DATE_FORMAT } from '~constants'
import { useSearchParams } from 'next/navigation'
import { generateCalendarStartAndEndDateForOneYear } from '../utils'
import { useEventsStore } from 'store/useEventsStore'
import { CALENDAR_EDP_NUMBER_OF_MONTHS } from 'constants/calendar'

export const useEventsForMultiArtist = (options: UseEventsForMultiArtistOptions) => {
  const { initialDate = new Date(), artists = [], endDate } = options
  const searchParams = useSearchParams()
  const [, startTransition] = useTransition()

  const initializedFromURL = useRef(false)

  const {
    selectedDate,
    selectedMonth,
    loading,
    errors,
    activeMonthList,
    filteredEventName,
    setSelectedDate,
    fetchEvents,
    setStartDate,
    setEndDate,
    setArtists,
    isNavigationClicked
  } = useEventsStore()

  /** -----------------------------
   *  SELECT DATE HANDLER
   *------------------------------*/
  const handleSelect = (newDate: DayjsInput) => {
    startTransition(() => setSelectedDate(newDate))
  }

  /** -----------------------------
   *  CALENDAR DATE RANGE HANDLING
   *------------------------------*/
  const calculatedCalendarRange = useMemo(() => {
    if (endDate) {
      return {
        startDate: dayjs(initialDate as string).format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR),
        endDate: dayjs(endDate as string).format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
      }
    }
    return generateCalendarStartAndEndDateForOneYear(selectedDate)
  }, [selectedDate, endDate, initialDate])

  /** -----------------------------
   *  MAIN EFFECT: SYNC URL + FETCH
   *------------------------------*/
  useEffect(() => {
    const startDateFromURL = searchParams.get('startdate')?.replace(/-/g, '/')

    // Determine the date to use
    let activeDate = selectedDate

    // Initialize from URL only once
    if (startDateFromURL && !initializedFromURL.current) {
      initializedFromURL.current = true
      const parsed = dayjs(startDateFromURL)
      if (parsed.isValid()) {
        setSelectedDate(parsed.toDate())
        activeDate = parsed.toDate()
      }
    }

    const parsedDate = dayjs(activeDate as string)
    const { startDate, endDate: naturalEndDate } = calculatedCalendarRange

    setStartDate(startDate)
    setEndDate(dayjs(naturalEndDate, DATE_FORMAT.SHORT_DATE_NO_SEPRATOR).toDate())
    setArtists(artists)

    /** Determine request range */
    let requestStart = startDate
    let requestEnd = naturalEndDate

    // Extend range if a filter is applied
    if (filteredEventName) {
      requestEnd = dayjs(naturalEndDate, DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
        .add(CALENDAR_EDP_NUMBER_OF_MONTHS, 'months')
        .format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)

      requestStart = isNavigationClicked
        ? parsedDate.startOf('month').format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
        : dayjs().startOf('month').format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
    } else {
      requestStart = selectedMonth
        ? dayjs(selectedMonth.value, DATE_FORMAT.MONTH_YEAR_VALUE).format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
        : dayjs().startOf('month').format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
    }

    fetchEvents(
      dayjs(requestStart, DATE_FORMAT.SHORT_DATE_NO_SEPRATOR).toDate(),
      dayjs(requestEnd, DATE_FORMAT.SHORT_DATE_NO_SEPRATOR).toDate(),
      artists,
      filteredEventName
    )
  }, [
    selectedDate,
    filteredEventName,
    searchParams, // safe to include - stable instance in Next.js
    artists.map(a => a.artistId).join(',') // avoids JSON.stringify
  ])

  return {
    selectedDate,
    selectedMonth,
    artists,
    errors,
    loading,
    activeMonthList,
    setSelectedDate: handleSelect
  }
}
